/**
 * useSociBabaTracker — captures 30+ behavioral micro-signals from every page.
 *
 * Signals recorded (batched, sent every 5s):
 *   view-level: impression, product_view, image_zoom, video_watch
 *   attention:  dwell, scroll_depth, scroll_pause, back_scroll, hover_pause,
 *               rage_click
 *   consider:   compare_add, wishlist_add, wishlist_remove, share_intent,
 *               review_read, qa_open, coupon_hover, price_check
 *   cart:       cart_add, cart_edit, cart_remove, cart_abandon,
 *               checkout_start, checkout_abandon, coupon_try, coupon_fail
 *   session:    session_start, session_end, revisit
 *
 * Consent aware — silently drops events if the user hasn't opted in.
 *
 * Uses REACT_APP_BACKEND_URL. Never blocks the UI; batching + navigator.sendBeacon
 * on page unload guarantees delivery even on fast tab-close.
 */
import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL as string;
const BATCH_MS = 5000;
const SESSION_KEY = "sb_session_v1";
const REVISIT_KEY = "sb_last_visit_v1";
const CONSENT_KEY = "sb_tracking_consent_v1";

type EventName =
  | "impression" | "product_view" | "image_zoom" | "video_watch"
  | "dwell" | "scroll_depth" | "scroll_pause" | "back_scroll"
  | "hover_pause" | "rage_click"
  | "compare_add" | "wishlist_add" | "wishlist_remove" | "share_intent"
  | "review_read" | "qa_open" | "coupon_hover" | "price_check"
  | "cart_add" | "cart_edit" | "cart_remove" | "cart_abandon"
  | "checkout_start" | "checkout_step_1" | "checkout_step_2" | "checkout_step_3"
  | "checkout_abandon" | "coupon_try" | "coupon_fail"
  | "revisit" | "direct_entry" | "cross_device_visit"
  | "search_query" | "filter_apply" | "sort_change" | "category_traversal"
  | "session_start" | "session_end" | "exit_page";

interface TrackerEvent {
  event_type: EventName;
  entity_type?: "product" | "brand" | "creator" | "channel" | "cart" | "search" | "page";
  entity_id?: string;
  metadata?: Record<string, any>;
  page_url?: string;
  referrer?: string;
}

// ---------- module-level singleton (shared across component instances) ------
let _queue: TrackerEvent[] = [];
let _flushTimer: any = null;
let _sessionId: string | null = null;
let _authToken: string | null = null;

function _getSessionId(): string {
  if (_sessionId) return _sessionId;
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      _sessionId = stored;
      return stored;
    }
  } catch { /* SSR-safe */ }
  const s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  try { sessionStorage.setItem(SESSION_KEY, s); } catch {}
  _sessionId = s;
  return s;
}

function _hasConsent(): boolean {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    // For guests we default to allowed (session-only, no PII). Logged-in users
    // are enforced server-side against socibaba_consent collection.
    return v !== "denied";
  } catch { return true; }
}

function _flush() {
  if (_queue.length === 0) return;
  const batch = _queue.splice(0, _queue.length);
  const body = JSON.stringify({ session_id: _getSessionId(), events: batch });
  const url = `${API}/api/events/ingest`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (_authToken) headers["Authorization"] = `Bearer ${_authToken}`;

  // Prefer sendBeacon on visibility change (survives tab close). Only works
  // without custom Authorization header, so we fall through to fetch when auth'd.
  if (!_authToken && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    } catch { /* fall through */ }
  }
  fetch(url, {
    method: "POST",
    headers,
    body,
    keepalive: true,
  }).catch(() => { /* silent — analytics never breaks UI */ });
}

function _schedule() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    _flush();
  }, BATCH_MS);
}

/** Push one event onto the queue. Consent-aware, silent on failure. */
export function trackSociBaba(ev: TrackerEvent) {
  if (!_hasConsent()) return;
  _queue.push({
    ...ev,
    page_url: ev.page_url ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
    referrer: ev.referrer ?? (typeof document !== "undefined" ? document.referrer : undefined),
  });
  if (_queue.length >= 25) _flush();
  else _schedule();
}

/** Manually pass the current bearer token (from auth context). */
export function setSociBabaAuthToken(token: string | null) {
  _authToken = token;
}

// ============================================================================
// Global mount — call once from App.tsx to wire session_start, revisit,
// direct_entry, scroll-depth, back-scroll, rage-click, visibility, unload.
// ============================================================================
export function useSociBabaTrackerGlobal() {
  const location = useLocation();
  const lastPathRef = useRef<string>("");
  const scrollDepthRef = useRef<number>(0);
  const lastScrollYRef = useRef<number>(0);
  const backScrollFiredRef = useRef<boolean>(false);
  const clickTsRef = useRef<number[]>([]);
  const pageEnterTsRef = useRef<number>(Date.now());

  useEffect(() => {
    // Session start once per tab
    if (!sessionStorage.getItem("sb_started_v1")) {
      sessionStorage.setItem("sb_started_v1", "1");
      trackSociBaba({ event_type: "session_start", entity_type: "page" });
    }

    // Revisit detection (last visit ≥ 5 min ago)
    try {
      const last = parseInt(localStorage.getItem(REVISIT_KEY) || "0", 10);
      const now = Date.now();
      if (last && now - last > 5 * 60 * 1000) {
        trackSociBaba({ event_type: "revisit", entity_type: "page",
          metadata: { minutes_since: Math.round((now - last) / 60000) } });
      }
      localStorage.setItem(REVISIT_KEY, String(now));
    } catch {}

    // Direct entry (no referrer, first pageview)
    if (!document.referrer) {
      trackSociBaba({ event_type: "direct_entry", entity_type: "page" });
    }

    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? Math.min(100, Math.round((scrollTop / docH) * 100)) : 0;
      if (pct > scrollDepthRef.current + 24) {
        scrollDepthRef.current = pct;
        trackSociBaba({ event_type: "scroll_depth", entity_type: "page",
          metadata: { pct } });
      }
      // Back-scroll = user scrolled UP significantly after having scrolled down
      if (scrollTop < lastScrollYRef.current - 400 && !backScrollFiredRef.current
          && scrollDepthRef.current > 40) {
        trackSociBaba({ event_type: "back_scroll", entity_type: "page",
          metadata: { from_pct: scrollDepthRef.current } });
        backScrollFiredRef.current = true;
        setTimeout(() => { backScrollFiredRef.current = false; }, 8000);
      }
      lastScrollYRef.current = scrollTop;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // Rage click detector — 3+ clicks in 500ms on same element
    const onClick = (e: MouseEvent) => {
      const now = Date.now();
      clickTsRef.current = clickTsRef.current.filter(t => now - t < 500);
      clickTsRef.current.push(now);
      if (clickTsRef.current.length >= 3) {
        trackSociBaba({ event_type: "rage_click", entity_type: "page",
          metadata: { x: e.clientX, y: e.clientY } });
        clickTsRef.current = [];
      }
    };
    window.addEventListener("click", onClick, { passive: true });

    // Dwell / exit_page on unload
    const onBeforeUnload = () => {
      const dwellMs = Date.now() - pageEnterTsRef.current;
      trackSociBaba({ event_type: "dwell", entity_type: "page",
        metadata: { dwell_ms: dwellMs, page: window.location.pathname } });
      trackSociBaba({ event_type: "exit_page", entity_type: "page" });
      _flush();
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    // Visibility change (mobile tab-away)
    const onVis = () => {
      if (document.visibilityState === "hidden") _flush();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("click", onClick);
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Per-route dwell + scroll reset on navigation
  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === lastPathRef.current) return;

    // Fire dwell for the prior page
    if (lastPathRef.current) {
      const dwellMs = Date.now() - pageEnterTsRef.current;
      trackSociBaba({ event_type: "dwell", entity_type: "page",
        metadata: { dwell_ms: dwellMs, page: lastPathRef.current } });
    }

    lastPathRef.current = path;
    pageEnterTsRef.current = Date.now();
    scrollDepthRef.current = 0;
    lastScrollYRef.current = 0;
    trackSociBaba({ event_type: "impression", entity_type: "page",
      metadata: { path } });
  }, [location.pathname, location.search]);
}

// ============================================================================
// Convenience hooks for specific entity tracking (product, cart, etc)
// ============================================================================

export function useProductViewTracker(productId?: string, extraMetadata?: Record<string, any>) {
  const startRef = useRef<number>(Date.now());
  useEffect(() => {
    if (!productId) return;
    startRef.current = Date.now();
    trackSociBaba({
      event_type: "product_view",
      entity_type: "product",
      entity_id: productId,
      metadata: extraMetadata || {},
    });
    return () => {
      const dwellMs = Date.now() - startRef.current;
      trackSociBaba({
        event_type: "dwell",
        entity_type: "product",
        entity_id: productId,
        metadata: { dwell_ms: dwellMs, ...extraMetadata },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);
}

/** Simple tracker returning a callable for imperative logging inside events. */
export function useSociBabaTracker() {
  return useCallback((ev: TrackerEvent) => trackSociBaba(ev), []);
}
